import zip from 'lodash/zip'
import * as observable from 'lib0/observable'
import { AsyncResource } from './AsyncResource'

class MediaPlaybackManager extends observable.Observable {
  constructor() {
    super()
    this._elems = new Map()
    this._timestamps = new Map()
  }

  register(media, elem, { genesis, elapsed }) {
    const { sid: id } = media

    elem.id = id

    if (this._elems.has(id)) return null

    elem.currentTime = this._getVideoCurrentTime(media, { genesis, elapsed })

    const asyncResource = new AsyncResource(id)
    elem.asyncResource = asyncResource
    elem.addEventListener('canplay', asyncResource.ready)
    elem.addEventListener('playing', asyncResource.ready)
    elem.addEventListener('waiting', asyncResource.waiting)

    this._elems.set(id, elem)
    return asyncResource
  }

  unregister({ sid: id }) {
    const elem = this._elems.get(id)
    if (!elem) return id

    elem.removeEventListener('canplay', elem.asyncResource.ready)
    elem.removeEventListener('playing', elem.asyncResource.ready)
    elem.removeEventListener('waiting', elem.asyncResource.waiting)
    this._saveTimestamp(id, elem.currentTime)

    this._elems.delete(id)
    return id
  }

  async play() {
    const elems = Array.from(this._elems.values())
    const currentTimes = elems.map((elem) => elem.currentTime)

    await Promise.all(elems.map((videoElem) => videoElem.play()))

    const currentTimesAfter = elems.map((elem) => elem.currentTime)

    /**
     * It could take various amount of times to play all the videos
     */
    zip(elems, currentTimes, currentTimesAfter).forEach(
      ([elem, before, after]) => {
        const diff = Math.abs(after - before) * 1000 // ms
        if (diff > 600) {
          elem.currentTime = before
        }
        elem.muted = false
      }
    )
  }

  pause() {
    for (let videoElem of this._elems.values()) {
      videoElem.pause()
      videoElem.muted = true
    }
  }

  seek(playbackState, media) {
    media
      .filter((m) => this._elems.has(m.sid))
      .forEach((m) => {
        const currentTime = this._getVideoCurrentTime(m, playbackState)

        if (currentTime === -1) return

        const elem = this._elems.get(m.sid)
        elem.currentTime = currentTime
      })
  }

  destroy() {
    this._elems = new Map()
    this._timestamps = new Map()
    super.destroy()
  }

  _saveTimestamp(_id, timestamp) {
    this._timestamps.set(_id, timestamp)
  }

  _getTimestamp(_id) {
    return this._timestamps.get(_id)
  }

  _getVideoCurrentTime(media, { genesis, elapsed }) {
    const { sid: id, start, end } = media

    const timestampCalcMs = elapsed - (start - genesis)
    if (end - genesis <= timestampCalc) {
      return -1
    }

    const timestampCalc = timestampCalcMs / 1000
    const timestampSaved = this._getTimestamp(id) || 0

    if (Math.abs(timestampCalc - timestampSaved) > 2) return timestampCalc
    else {
      return timestampSaved
    }
  }
}

export default MediaPlaybackManager
